import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type EstimateStatus, type EstimateSummary, listEstimatesSortBySchema } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { formatDay, formatKm, formatMoney } from '../../lib/format';
import { useListSearchParams } from '../../lib/list-search-params';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { ESTIMATE_STATUSES, estimateStatusTone, vehicleTitle } from './estimates-display';

/**
 * `/estimates` (plan §7).
 *
 * **Every** estimate is here, whichever way it ended: the status filter starts empty on purpose,
 * because an appraisal that did not become a purchase is exactly the record a salesperson comes back to
 * six months later (plan §3.1). The dashboard cards link in with `?status=` pre-filled.
 *
 * Page, order and every filter live in the URL and are answered by the service, so the list is longer
 * than one page and comes back where it was left (plan §11 fix 12).
 */
export const EstimatesListPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const params = useListSearchParams();

  const search = params.value('q');
  const statusFilter = params.value('status');
  const templateFilter = params.value('templateId');
  const assigneeFilter = params.value('assignedUserId');
  const updatedFrom = params.value('updatedFrom');
  const updatedTo = params.value('updatedTo');
  const sortBy = params.sortBy(listEstimatesSortBySchema.options, 'updatedAt');
  const sortDirection = params.sortDirection('desc');

  const templatesQuery = useQuery(orpc.templates.list.queryOptions({ input: { page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc' } }));

  const listQuery = useQuery(
    orpc.estimates.list.queryOptions({
      input: {
        page: params.page,
        pageSize: params.pageSize,
        sortBy,
        sortDirection,
        q: search.trim() === '' ? undefined : search.trim(),
        status: statusFilter === '' ? undefined : [statusFilter as EstimateStatus],
        templateId: templateFilter === '' ? undefined : templateFilter,
        assignedUserId: assigneeFilter === '' ? undefined : assigneeFilter,
        updatedFrom: updatedFrom === '' ? undefined : updatedFrom,
        updatedTo: updatedTo === '' ? undefined : updatedTo,
      },
    }),
  );

  /** The assignee list is drawn from the rows on screen: there is no "list every user" call for SALES. */
  const assignees = useMemo(() => {
    const seen = new Map<string, string>();

    for (const row of listQuery.data?.items ?? []) {
      if (row.assignedUserId && row.assignedUserName) {
        seen.set(row.assignedUserId, row.assignedUserName);
      }
    }

    return [...seen.entries()].map(([id, name]) => ({ value: id, label: name }));
  }, [listQuery.data]);

  const columns = useMemo<DataTableColumns<EstimateSummary>>(
    () => [
      {
        id: 'estimateNo',
        header: t('estimates.columnEstimate'),
        accessorFn: (row) => row.estimateNo,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-medium text-foreground">{row.original.estimateNo}</p>
            <p className="truncate text-xs text-muted-foreground">{formatDay(row.original.updatedAt)}</p>
          </div>
        ),
      },
      {
        id: 'customer',
        header: t('estimates.columnCustomer'),
        // The service sorts this list, and its `sortBy` enum has no customer, vehicle or status order.
        enableSorting: false,
        accessorFn: (row) => row.customerName ?? '',
        cell: ({ row }) => (
          <InfoCell
            title={row.original.customerName ?? t('estimates.customerNotRecorded')}
            subline={row.original.customerPhone}
          />
        ),
      },
      {
        id: 'vehicle',
        header: t('estimates.columnVehicle'),
        enableSorting: false,
        accessorFn: (row) => vehicleTitle(row),
        cell: ({ row }) => (
          <InfoCell
            title={vehicleTitle(row.original) === '' ? t('common.notRecorded') : vehicleTitle(row.original)}
            subline={[row.original.modelYear ?? null, row.original.odometerKm === null ? null : formatKm(row.original.odometerKm)]
              .filter((part) => part !== null)
              .join(' · ')}
          />
        ),
      },
      {
        id: 'customerAskingPrice',
        header: t('estimates.columnAsking'),
        accessorFn: (row) => row.customerAskingPrice ?? 0,
        cell: ({ row }) => (
          <span
            dir="ltr"
            className="inline-block tabular-nums text-foreground"
          >
            {formatMoney(row.original.customerAskingPrice)}
          </span>
        ),
      },
      {
        id: 'ctaOffer',
        header: t('estimates.columnOffer'),
        accessorFn: (row) => row.ctaOffer ?? 0,
        cell: ({ row }) => (
          <span
            dir="ltr"
            className="inline-block tabular-nums text-foreground"
          >
            {formatMoney(row.original.ctaOffer)}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('estimates.columnStatus'),
        enableSorting: false,
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <StatusText
            label={t(`estimates.statuses.${row.original.status}`)}
            tone={estimateStatusTone(row.original.status)}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('estimates.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('estimates.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/estimates/new')}>
          <Plus className="size-4" />
          {t('estimates.new')}
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <label
            htmlFor="estimates-search"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.search')}
          </label>
          <Input
            id="estimates-search"
            value={search}
            placeholder={t('estimates.searchPlaceholder')}
            onChange={(event) => params.update({ q: event.target.value })}
          />
        </div>
        <div className="w-48">
          <label
            htmlFor="estimates-status"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.columnStatus')}
          </label>
          <Select
            id="estimates-status"
            value={statusFilter}
            placeholder={t('estimates.allStatuses')}
            onChange={(event) => params.update({ status: event.target.value })}
            options={ESTIMATE_STATUSES.map((status) => ({ value: status, label: t(`estimates.statuses.${status}`) }))}
          />
        </div>
        <div className="w-48">
          <label
            htmlFor="estimates-template"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.template')}
          </label>
          <Select
            id="estimates-template"
            value={templateFilter}
            placeholder={t('common.all')}
            onChange={(event) => params.update({ templateId: event.target.value })}
            options={(templatesQuery.data?.items ?? []).map((template) => ({ value: template.id, label: template.name }))}
          />
        </div>
        <div className="w-44">
          <label
            htmlFor="estimates-assignee"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.assignee')}
          </label>
          <Select
            id="estimates-assignee"
            value={assigneeFilter}
            placeholder={t('common.all')}
            onChange={(event) => params.update({ assignedUserId: event.target.value })}
            options={assignees}
          />
        </div>
        <div className="w-40">
          <label
            htmlFor="estimates-updated-from"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.updatedFrom')}
          </label>
          <Input
            id="estimates-updated-from"
            type="date"
            dir="ltr"
            value={updatedFrom}
            onChange={(event) => params.update({ updatedFrom: event.target.value })}
          />
        </div>
        <div className="w-40">
          <label
            htmlFor="estimates-updated-to"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.updatedTo')}
          </label>
          <Input
            id="estimates-updated-to"
            type="date"
            dir="ltr"
            value={updatedTo}
            onChange={(event) => params.update({ updatedTo: event.target.value })}
          />
        </div>
      </div>

      {listQuery.isError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>
      ) : null}

      <DataTable
        columns={columns}
        data={listQuery.data?.items ?? []}
        isLoading={listQuery.isLoading}
        emptyMessage={t('estimates.empty')}
        pagination={{ page: params.page, pageSize: params.pageSize, total: listQuery.data?.meta.total ?? 0, onPageChange: params.setPage }}
        sorting={{ sortBy, sortDirection, onSortChange: params.setSort }}
        onRowClick={(row) => navigate(`/estimates/${row.id}`)}
      />
    </div>
  );
};
